import axios from 'axios';

if (!process.env.REACT_APP_BACKEND_URL) {
  // Fail loudly at module load so a misconfigured build doesn't silently send
  // requests to "undefined/api/...".
  // eslint-disable-next-line no-console
  console.error('REACT_APP_BACKEND_URL is not set; API requests will fail.');
}

const API = axios.create({
  baseURL: (process.env.REACT_APP_BACKEND_URL || '') + '/api',
  // Auth is cookie-based — withCredentials must be true for the browser to
  // attach the httpOnly auth_token cookie on cross-origin (CORS) requests.
  withCredentials: true,
});

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Extract a user-presentable error message from an axios error.
 *
 * The backend convention (see AppError in backend/server.py) is that the
 * response body is always `{ detail: string, ...extras }`. Extras like
 * `hint`, `failing_checks`, or `current_status` are appended on new lines
 * so toasts surface the structured context without rendering [object Object].
 */
export function extractErrorMessage(err, fallback = 'Request failed') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;

  // FastAPI may serialize validation errors as detail=array-of-objects.
  if (Array.isArray(data.detail)) {
    return data.detail
      .map(e => `${e.loc?.slice(-1)[0] || ''}: ${e.msg || ''}`.trim())
      .filter(Boolean)
      .join('\n') || fallback;
  }

  const detail = typeof data.detail === 'string' ? data.detail : fallback;
  const extras = [];

  if (Array.isArray(data.failing_checks) && data.failing_checks.length) {
    extras.push(`Failing: ${data.failing_checks.join(', ')}`);
  }
  if (data.current_status) {
    extras.push(`Current status: ${data.current_status}`);
  }
  if (typeof data.hint === 'string' && data.hint) {
    extras.push(data.hint);
  }

  return extras.length ? `${detail}\n${extras.join('\n')}` : detail;
}

// CSRF double-submit: read the csrf_token cookie and echo it back in the
// X-CSRF-Token header on every mutating request. The cookie was set by the
// backend on login.
API.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const path = window.location.pathname;
    // 401: not authenticated -> bounce to login (unless we're already there).
    // 403 with X-Auth-Reason: must_change_password -> redirect to change page.
    if (status === 401 && path !== '/login') {
      window.location.href = '/login';
    } else if (status === 403
               && err.response?.headers?.['x-auth-reason'] === 'must_change_password'
               && path !== '/change-password') {
      window.location.href = '/change-password';
    }
    return Promise.reject(err);
  }
);

export default API;
